import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { Toggle } from 'components/tailwind-ui/Toggle';
import React from 'react';
import { getShippersListCount, WatchList, WatchListFilters } from 'services/clients/watchList';
import { InformationalTooltip } from 'components/InformationalTooltip';
import { UnwatchedState } from './types';
import { useFilterSearchParams } from './utilities';

const ToggleUnwatchedStateMapping: { [key in UnwatchedState]: boolean } & {
  [key: string]: boolean;
} = {
  [UnwatchedState.OnlyUnwatched]: true, // Not used
  [UnwatchedState.OnlyWatched]: false,
  [UnwatchedState.WatchedAndUnwatched]: true,
};

interface Props {
  filters: WatchListFilters;
  moreFiltersOpen: boolean;
}

export const UnwatchToggleBanner: React.FC<Props> = (props) => {
  const { filters, moreFiltersOpen } = props;
  const { searchParams, setSearchParams } = useFilterSearchParams(true);

  const setShowUnwatched = React.useCallback(
    (value: boolean) => {
      if (value === true) {
        setSearchParams({ unwatchedState: UnwatchedState.WatchedAndUnwatched });
      } else {
        setSearchParams({ unwatchedState: UnwatchedState.OnlyWatched });
      }
    },
    [setSearchParams]
  );

  const unwatchedCountQuery: UseQueryResult<WatchList> = useQuery({
    queryKey: ['unwatchedCount', filters],
    queryFn: ({ signal }) => {
      const filterObj = { ...filters, unwatched: true };
      delete filterObj.unwatchedState; // This will confuse the API
      return getShippersListCount({ filters: filterObj, signal });
    },
    keepPreviousData: moreFiltersOpen,
    enabled: !moreFiltersOpen,
  });

  return (
    <div className="bg-surface-accent-2 px-4 py-2 w-full border-t-1 flex space-x-2 items-center shadow-sm">
      <Toggle
        value={ToggleUnwatchedStateMapping[searchParams.unwatchedState]}
        onChange={setShowUnwatched}
      />
      <h5 className="text-text-accent-3 font-medium">
        Show inactive containers ({unwatchedCountQuery.data?.count || 0})
      </h5>
      <InformationalTooltip>
        Inactive containers are containers that we have not found new updates for in more than 45
        days.
      </InformationalTooltip>
    </div>
  );
};
